import { NavigateFunction } from 'react-router'
import type { AnyAction, Dispatch } from '@reduxjs/toolkit'
import path from 'path-browserify'
import { all, call, put, take, takeLatest } from 'redux-saga/effects'
import { v4 as uuidv4 } from 'uuid'

import { createPayload } from '../lib/create-payload'
import { humanizeSeconds } from '../lib/formatters'
import { getBaseUrl } from '../lib/helpers'
import { createUploadFileChannel } from './file-channel'
import MultipartUploader, { UploadMetadata } from './multipart-uploader'
import type { FileType, Metadata, TrackingRecordType } from './types'
import {
  uploadFailure,
  uploadFinished,
  uploadProgress,
  uploadRequesting,
  uploadSuccess
} from './upload-actions'
import { categories, UPLOAD_REQUEST } from './upload-constants'
import { isS3Approved } from './upload-selectors'

function* uploadFiles(
  dispatch: Dispatch,
  navigate: NavigateFunction,
  files: FileType[],
  meta: Metadata,
  trackingInfo?: TrackingRecordType[],
): Generator<any> {
  yield put(uploadRequesting())
  const foundFiles: Record<string, number> = {}
  const results: any = yield all(
    files.map((file) => {
      const newMeta = { ...meta }
      if (trackingInfo !== undefined && trackingInfo.length) {
        const currTrackInfoArr = trackingInfo.filter((x: TrackingRecordType) => {
          return x.Filename === file.file.name
        })
        if (!currTrackInfoArr.length) {
          newMeta.trackingId = 'not found'
          newMeta.batchId = 'not found'
        } else {
          // Due to the tracking endpoint returning tracking ID's by file name,
          // and the fact that the end-user can upload multiple files with the same
          // file name, this code ensures that no two files with the same name
          // uses the same tracking ID
          const key = currTrackInfoArr[0].Filename
          if (key in foundFiles) {
            foundFiles[key]++
          } else {
            foundFiles[key] = 0
          }
          newMeta.trackingId = currTrackInfoArr[foundFiles[key]].TrackingId
          newMeta.batchId = currTrackInfoArr[foundFiles[key]].BatchId
        }
      }

      if (isS3Approved(file.file)) return call(uploadS3File, dispatch, file, newMeta)
      return call(uploadFile, file, newMeta)
    })
  )

  if (results.some((r: any) => r)) {
    yield put(uploadSuccess())
  }
  yield put(uploadFinished())

  navigate(`${window.travURI}confirmation`)
}

const createMetaData = (
  file: FileType,
  meta: Metadata,
  clientAddress?: string
): UploadMetadata | undefined => {
  const handler = {
    contactUId: '',
    officeCode: ''
  }
  if (meta.claim) {
    handler.contactUId = meta.claim.contactUid
    handler.officeCode = meta.claim.handlerAfo
  }

  try {
    const awsMetaData: Record<string, string> = {
      filename: encodeURI(file.file.name),
      claim: meta.claim.claimNumber,
      claimant: meta.claim.claimantNumber,
      senderName: meta.claim.digitalFileUpload.senderEmail,
      role: meta.claim.digitalFileUpload.role,
      dateOfLoss: meta.claim.digitalFileUpload.lossDate,
      contentType: file.file.type || 'video/unknown',
      source: 'ExternalUpload',
      ...(handler.officeCode ? { handlerAfo: handler.officeCode } : undefined),
      ...(handler.contactUId ? { contactUId: handler.contactUId.toString() } : undefined),
      categoryDescription:
        (categories.find((cat) => cat.value === file.category) || {}).display ||
        'No Category Description',
      clientAddress: clientAddress || 'None'
    }

    // AWS requires that metadata fields have an assigned value
    // (they cannot be `undefined`). Thus, we are only passing on the tracking
    // information if the values have been defined.
    if (meta.trackingId) {
      awsMetaData.trackingId = meta.trackingId
    }

    if (meta.batchId) {
      awsMetaData.batchId = meta.batchId
    }

    return awsMetaData
  } catch {
    return
  }
}

function* uploadFile(file: FileType, meta: Metadata): Generator<any> {
  const payload = createPayload(file, meta)
  const channel: any = yield call(
    createUploadFileChannel,
    `${getBaseUrl()}claimfileuploaderapi/v1/uploads`,
    payload
  )

  while (true) {
    const result: any = yield take(channel)
    const { progress = 0, timeLeft, err, success } = result

    if (err) {
      yield put(uploadFailure(file.file.name, err, meta.trackingId, meta.batchId))
      return false
    }

    if (success) {
      yield put(uploadProgress(file.file.name, 1, timeLeft, meta.trackingId, meta.batchId))
      return true
    }

    if (progress < 1) {
      yield put(uploadProgress(file.file.name, progress, timeLeft, meta.trackingId, meta.batchId))
    }
  }
}

function* uploadS3File(dispatch: Dispatch, file: FileType, meta: Metadata): Generator<any> {
  const uploadInfo: any = yield fetch(
    `${getBaseUrl()}claimfileuploaderapi/v1/video/upload/`
      + 'destination',
    {
      credentials: 'omit'
    }
  ).then((x) => x.json())

  const bucketName = uploadInfo.bucketName
  const clientAddress = uploadInfo.clientAddress

  const uploader = MultipartUploader.fileUploader({
    authURL: `${getBaseUrl()}claimfileuploaderapi/v1/video/sign`,
    bucket: bucketName,
    // optional. default is 2
    retryCount: 3,
    // optional. default is 250ms
    retryDelay: 1e3
  })

  const trackingInfo = {
    trackingId: meta.trackingId,
    batchId: meta.batchId
  }

  try {
    const startTime = new Date().getTime()
    let partCount = 0

    dispatch({
      type: 'UPLOAD_PROGRESS',
      filename: file.file.name,
      progress: 0.01,
      timeLeft: undefined,
      ...trackingInfo
    })

    //ISO 8601 datetime with extra chars and secs removed
    const datestr =
      new Date()
        .toISOString()
        .replace(/[^0-9]/gi, '')
        .slice(0, -3) + 'Z'

    yield uploader.uploadFile({
      file: file.file,
      key: path.join(datestr, uuidv4()),
      onProgress: ({ totalParts }: { part: number, totalParts: number }) => {
        partCount++
        const progress = partCount / totalParts
        const timeSpent = new Date().getTime() - startTime
        const remainingTime = Math.round((timeSpent / progress - timeSpent) / 1000)
        const secs = humanizeSeconds(remainingTime)
        const action = { filename: file.file.name, progress, timeLeft: secs, ...trackingInfo }
        dispatch({ type: 'UPLOAD_PROGRESS', ...action })
      },
      metadata: createMetaData(file, meta, clientAddress),
      parallelAmount: 2
    })
  } catch (e) {
    dispatch({ type: 'UPLOAD_FAILURE', filename: file.file.name, error: e, ...trackingInfo })
  }
}

function* uploadRequestWatcher(dispatch: Dispatch): Generator<any> {
  yield takeLatest(UPLOAD_REQUEST, function*(action: AnyAction) {
    yield call(
      uploadFiles,
      dispatch,
      action.navigate,
      action.files,
      action.meta,
      action.trackingInfo
    )
  })
}

export {
  createMetaData,
  uploadFile,
  uploadFiles,
  uploadRequestWatcher,
  uploadS3File
}
